/* eslint-disable no-return-assign */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { Header, Footer } from "@components/index";

import Home from "@components/Home";
import TransactionIntro from "@components/TransactionIntro";
import PointUsage from "@components/PointUsage";
import Preview from "@components/Preview";
import ToConnect from "@components/ToConnect";
import StockIntro from "@components/StockIntro";
import StockPreview from "@components/StockPreview";
import { Tab } from "@customType/Tab";
import { cls } from "src/theme/colors";
import { styled } from "styled-components";

// 디자인 기준 가로폭. 화면이 이보다 좁으면 같은 비율로 축소해 가로 스크롤 없이 한눈에 보이게 한다.
const DESIGN_WIDTH = 2200;

// 보여줄 탭은 주소의 해시(#myd, #mystock)로 정한다. 경로가 아니라 해시라서 새로고침하거나 링크로 들어와도 같은 탭이 열린다.
const getTabFromHash = (): Tab => {
  // 대소문자가 다르거나 뒤에 다른 값이 붙어 있어도(#MYD, #myd/, #mystock?utm_source=...) 같은 탭으로 본다.
  const hash = window.location.hash.slice(1).toLowerCase().split(/[?&/]/)[0];
  return hash === "myd" || hash === "mystock" ? hash : "home";
};

// 스크린 리더가 탭이 바뀐 것을 알 수 있도록 본문 영역에 붙이는 화면 이름
const SCREEN_NAMES: Record<Tab, string> = {
  home: "myd.world 홈",
  myd: "마이디",
  mystock: "마이주식",
};

function App() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const [fit, setFit] = useState({ scale: 1, height: 0 });
  // 화면에 실제로 적용된 배율과, 배율이 바뀔 때 보던 위치를 유지하기 위해 옮겨 갈 스크롤 위치
  const appliedScaleRef = useRef(1);
  const pendingScrollYRef = useRef<number | null>(null);
  const updateFitRef = useRef<(() => void) | null>(null);
  const [tab, setTab] = useState<Tab>(getTabFromHash);
  const shownTabRef = useRef(tab);

  useEffect(() => {
    if (isMobile) {
      window.location.href = `https://m.myd.world/${window.location.hash}`;
    }
  }, []);

  useEffect(() => {
    // 탭 링크 클릭과 브라우저 뒤로/앞으로 가기를 모두 따라간다.
    const syncTab = () => setTab(getTabFromHash());

    window.addEventListener("hashchange", syncTab);
    window.addEventListener("popstate", syncTab);

    return () => {
      window.removeEventListener("hashchange", syncTab);
      window.removeEventListener("popstate", syncTab);
    };
  }, []);

  useLayoutEffect(() => {
    // 탭이 바뀌면 새 화면의 맨 위부터 보여준다.
    if (shownTabRef.current === tab) return;
    shownTabRef.current = tab;
    // 페이지 높이를 새 화면에 맞게 바로 다시 잡는다. ResizeObserver 를 기다리면 그 사이 한 프레임이 이전 화면 높이로 잘려 보이고,
    // 뒤로/앞으로 가기 때 브라우저가 되돌려 놓는 스크롤 위치도 이전 화면 높이에 막혀 엉뚱한 곳에서 멈춘다.
    updateFitRef.current?.();
    window.scrollTo(0, 0);
    // 키보드·스크린 리더 사용자가 새 화면의 본문부터 이어서 읽을 수 있게 한다.
    mainRef.current?.focus({ preventScroll: true });
  }, [tab]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const container = containerRef.current;
    if (!viewport || !container) return undefined;

    const updateFit = () => {
      // 브라우저 확대/축소 시 폭이 소수점일 수 있어 clientWidth(정수) 대신 실제 폭을 사용한다.
      const scale = Math.min(1, viewport.getBoundingClientRect().width / DESIGN_WIDTH);
      // transform은 레이아웃 높이를 줄여주지 않으므로 축소된 높이를 직접 맞춰준다.
      // 소수점으로 남는 맨 아래 줄은 푸터 밑에 옅은 선으로 보이므로, 화면 픽셀 단위로 내림해서 잘라낸다.
      const dpr = window.devicePixelRatio || 1;
      const height = Math.floor(container.offsetHeight * scale * dpr) / dpr;
      // 배율이 바뀌면 페이지 높이도 같이 바뀌므로, 높이가 바뀌기 전의 스크롤 위치를 새 배율로 환산해 둔다.
      pendingScrollYRef.current =
        scale !== appliedScaleRef.current && window.scrollY > 0
          ? window.scrollY * (scale / appliedScaleRef.current)
          : null;
      setFit((prev) => (prev.scale === scale && prev.height === height ? prev : { scale, height }));
    };

    updateFit();
    updateFitRef.current = updateFit;
    window.addEventListener("resize", updateFit);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateFit);
    observer?.observe(viewport);
    observer?.observe(container);

    return () => {
      updateFitRef.current = null;
      window.removeEventListener("resize", updateFit);
      observer?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    appliedScaleRef.current = fit.scale;
    if (pendingScrollYRef.current === null) return;
    window.scrollTo(window.scrollX, pendingScrollYRef.current);
    pendingScrollYRef.current = null;
  }, [fit.scale]);

  const isScaled = fit.scale < 1;

  return (
    <S.Page>
      <S.Viewport ref={viewportRef} style={isScaled ? { height: fit.height } : undefined}>
        <S.Container ref={containerRef} style={isScaled ? { transform: `scale(${fit.scale})` } : undefined}>
          <Header tab={tab} />
          <S.Main ref={mainRef} tabIndex={-1} aria-label={SCREEN_NAMES[tab]}>
            {tab === "home" && <Home />}
            {tab === "myd" && (
              <>
                <TransactionIntro />
                <PointUsage />
                <Preview />
                <ToConnect />
              </>
            )}
            {tab === "mystock" && (
              <>
                <StockIntro />
                <StockPreview />
              </>
            )}
          </S.Main>
          <Footer />
        </S.Container>
      </S.Viewport>
    </S.Page>
  );
}

export default App;

const S = {
  // 화면이 축소된 페이지보다 길 때(세로 모니터 등) 푸터 아래 남는 공간이 푸터와 이어져 보이도록 푸터 색으로 채운다.
  Page: styled.div`
    min-height: 100vh;
    background: #333333;
  `,
  Viewport: styled.div`
    width: 100%;
    background: ${cls.white};
    /* hidden 은 스크립트·링크(#:~:text 등)로는 스크롤이 되어 페이지가 잘릴 수 있어 clip 을 쓴다. hidden 은 미지원 브라우저용. */
    overflow: hidden;
    overflow: clip;
  `,
  Container: styled.div`
    position: relative;
    min-width: ${DESIGN_WIDTH}px;
    transform-origin: 0 0;
  `,
  // 탭이 바뀔 때 포커스를 받기만 하는 영역이라 포커스 테두리는 그리지 않는다.
  Main: styled.main`
    display: block;
    outline: none;
  `,
};
